import { LoginState } from "@/app/store/modules/login.state";
import { AuthClient, UserLoginAttemptModel } from "@/app/api/battery-portal-api";
import { Notifier } from "@/common/utils/notifier";
import { CryptoHelper } from "@/app/utils/crypto-helper";
import { AppConfigState } from "@/app/store/modules/appconfig.state";
import { LICENSE_BANNER_DISMISSED_KEY } from "@/app/vues/components/license-warning-banner.vue";
import * as Sentry from "@sentry/vue";
const userTokenKey = window.userTokenKey;
const authClient = new AuthClient(window.apiUrl);
let userToken = new LoginState();
const encrypted = localStorage.getItem(userTokenKey);
if (encrypted) {
    try {
        const decrypted = CryptoHelper.decryptSync(encrypted);
        userToken = LoginState.fromJson(decrypted);
    }
    catch (ex) {
        console.error("Failed to decrypt user token:", ex);
        localStorage.removeItem(userTokenKey);
        userToken = new LoginState();
    }
}
function updateUserToken() {
    let encrypted = localStorage.getItem(userTokenKey);
    if (encrypted) {
        try {
            const decrypted = CryptoHelper.decryptSync(encrypted);
            let auth = JSON.parse(decrypted);
            if (auth.user) {
                auth.user.mustAcceptTerms = userToken.mustAcceptTerms || false;
                auth.user.useImperialMeasurementSystem = userToken.useImperialMeasurementSystem;
                auth.user.leftMenuState = userToken.leftMenuState;
                localStorage.setItem(userTokenKey, CryptoHelper.encryptSync(JSON.stringify(auth)));
            }
        }
        catch (ex) {
            console.error("Failed to update user token:", ex);
        }
    }
}
export const state = userToken;
const getters = {
    isLoggedIn(state) {
        return state.authenticated;
    },
    isPowerShield(state) {
        if (!state)
            return false;
        return state.isPowerShield;
    },
    isPowerShieldAdministrator(state) {
        if (!state)
            return false;
        return state.isPowerShieldAdministrator;
    },
    isClientAdministrator(state) {
        if (!state)
            return false;
        return state.isClientAdministrator;
    },
    isClientInstaller(state) {
        if (!state)
            return false;
        return state.isClientInstaller;
    },
    isClientAdministratorUser(state) {
        if (!state)
            return false;
        return state.isClientAdministratorUser;
    },
    isUsingImperialMeasurementSystem(state) {
        if (!state)
            return false;
        return state.useImperialMeasurementSystem;
    },
    isAcceptanceOfTermsRequired: (state) => () => {
        if (!state)
            return false;
        return state.mustAcceptTerms;
    },
    userId(state) {
        if (!state)
            return 0;
        return state.userId;
    },
};
export const mutations = {
    loginAttempt(state) {
        state.attemptingLogin = true;
        state.authenticated = false;
    },
    loginSuccess(state) {
        state.attemptingLogin = false;
    },
    loginFailed(state) {
        state.reset();
    },
    logout(state) {
        state.reset();
    },
    setLeftMenuState(state, leftMenuState) {
        state.leftMenuState = leftMenuState;
    },
    setImperialMeasurementPreference(state, useImperialMeasurementSystem) {
        state.useImperialMeasurementSystem = useImperialMeasurementSystem;
    },
    setTermsAcceptanceRequirement(state, mustAcceptTerms) {
        state.mustAcceptTerms = mustAcceptTerms;
    }
};
export const actions = {
    login({ commit }, login) {
        commit("loginAttempt");
        let attempt = new UserLoginAttemptModel();
        attempt.userName = login.userName;
        attempt.password = login.password;
        authClient
            .login(attempt)
            .then(auth => {
            var _a, _b, _c;
            commit("loginSuccess");
            localStorage.setItem(userTokenKey, CryptoHelper.encryptSync(JSON.stringify(auth)));
            // Reset any previous banner dismissal so warnings are re-shown after a fresh login.
            sessionStorage.removeItem(LICENSE_BANNER_DISMISSED_KEY);
            Sentry.setUser({
                id: (_a = auth === null || auth === void 0 ? void 0 : auth.user) === null || _a === void 0 ? void 0 : _a.userId.toString(),
                username: ((_b = auth === null || auth === void 0 ? void 0 : auth.user) === null || _b === void 0 ? void 0 : _b.userName) || "",
                email: ((_c = auth === null || auth === void 0 ? void 0 : auth.user) === null || _c === void 0 ? void 0 : _c.emailAddress) || ""
            });
            if (auth.requires2FA)
                window.location.href = window.twoFactorAuthUrl;
            else if (login.returnUrl) {
                window.location.href = decodeURIComponent(login.returnUrl);
            }
            else {
                window.location.href = window.baseUrl;
            }
        })
            .catch((ex) => {
            console.error('login error', ex);
            commit("loginFailed");
            Notifier.onServerError(ex);
        });
    },
    logout({ commit }) {
        authClient.logout().then(() => {
            localStorage.removeItem(userTokenKey);
            localStorage.removeItem(AppConfigState.configKey);
            Sentry.setUser(null);
            commit("logout");
        });
    },
    logoutRedirect({ commit, dispatch }) {
        dispatch("logout").then(() => {
            window.location.href = window.baseUrl;
        }).catch(() => {
            window.location.href = window.baseUrl;
        });
    },
    setLeftMenuState({ commit }, leftMenuState) {
        userToken.leftMenuState = leftMenuState;
        updateUserToken();
        commit("setLeftMenuState", leftMenuState);
    },
    setImperialMeasurementPreference({ commit }, useImperialMeasurementSystem) {
        userToken.useImperialMeasurementSystem = useImperialMeasurementSystem;
        updateUserToken();
        commit("setImperialMeasurementPreference", useImperialMeasurementSystem);
    },
    setTermsAcceptanceRequirement({ commit }, mustAcceptTerms) {
        userToken.mustAcceptTerms = mustAcceptTerms;
        updateUserToken();
        commit("setTermsAcceptanceRequirement", mustAcceptTerms);
    }
};
const namespaced = true;
export const loginModule = {
    namespaced,
    state,
    actions,
    mutations,
    getters
};
